import { clientConfig, getSdk } from "@quickpoint.me/shared";
import { getAuth } from "firebase/auth";
import { GraphQLClient } from "graphql-request";
import type { ImageStatus } from "../../displayComponents/ImageGenerationProgress";
import type { ImagePickerSelection, ImagePickerSlot } from "../../state/aiChatAtoms";
import { updateFiles } from "../messageBus";

interface ProcessOptions {
	ownerName: string;
	stackId: string;
	hostId: string;
	onImageProgress?: (updater: (prev: ImageStatus[]) => ImageStatus[]) => void;
	onQueueInit?: (queue: ImageStatus[]) => void;
}

/** Parsed info about a single GENERATE: placeholder */
export interface GeneratePlaceholder {
	fullMatch: string;
	keyword: string;
	imageModifier: string;
	prompt: string;
}

/** Extracts GENERATE: placeholders from jadle code */
export function findGeneratePlaceholders(jadleCode: string): { code: string; placeholders: GeneratePlaceholder[] } {
	const code = jadleCode.replace(/^(---|-->|===|\|)[ \t]+$/gm, "$1");
	const regex = /(image|background):\s*(image\s*)?"?GENERATE:\s*(.*)/gi;
	const matches = [...code.matchAll(regex)];

	const placeholders = matches.map((m) => {
		let prompt = m[3].trim();
		if (prompt.endsWith('"')) prompt = prompt.slice(0, -1);
		if (prompt.startsWith('"')) prompt = prompt.slice(1);
		return {
			fullMatch: m[0],
			keyword: m[1],
			imageModifier: m[2] ? "image " : "",
			prompt,
		};
	});

	return { code, placeholders };
}

/**
 * Text lines of the page surrounding a placeholder — the picker's context
 * panel ("which slide is this image for?"). Keyword prefixes are stripped;
 * media/control/modifier lines are dropped.
 */
export function extractPlaceholderContext(
	code: string,
	placeholder: GeneratePlaceholder,
): { before: string[]; after: string[] } {
	const lines = code.split("\n");
	const idx = lines.findIndex((l) => l.includes(placeholder.fullMatch));
	if (idx === -1) return { before: [], after: [] };

	let start = 0;
	let end = lines.length - 1;
	for (let i = idx - 1; i >= 0; i--) {
		if (lines[i].trim().startsWith("===")) {
			start = i + 1;
			break;
		}
	}
	for (let i = idx + 1; i < lines.length; i++) {
		if (lines[i].trim().startsWith("===")) {
			end = i - 1;
			break;
		}
	}

	const displayText = (line: string): string | null => {
		const t = line.trim();
		if (!t) return null;
		if (/^(===|---|-->|\||\^\^\^|vvv|```)/.test(t)) return null;
		if (/^(image|background|video|audio|chart|map|filter|moveBy|autoplay|autospace|blank|line|form|pause):/i.test(t)) return null;
		// indented modifier lines (width:, align:, …)
		if (/^\s{2,}/.test(line) && /^[a-zA-Z]+:/.test(t)) return null;
		const m = t.match(/^(supertitle|title|subtitle|columnTitle|button|read|storyName|##|#|\*\*|\*):\s*(.*)$/i);
		return m ? m[2] : t;
	};

	const collect = (from: number, to: number) => {
		const out: string[] = [];
		for (let i = from; i <= to; i++) {
			const v = displayText(lines[i]);
			if (v) out.push(v);
		}
		return out;
	};

	return {
		before: collect(start, idx - 1).slice(-4),
		after: collect(idx + 1, end).slice(0, 4),
	};
}

/** Searches public domain sources for each placeholder. Returns picker slots. */
export async function searchForPlaceholders(
	placeholders: GeneratePlaceholder[],
): Promise<ImagePickerSlot[]> {
	const auth = getAuth();
	const jwt = await auth.currentUser?.getIdToken();
	if (!jwt) return placeholders.map((p) => ({ prompt: p.prompt, results: [], status: "ready" as const }));

	const client = new GraphQLClient(clientConfig.gqlGenerateAiImage());
	const sdk = getSdk(client);

	const results = await Promise.allSettled(
		placeholders.map(async (p) => {
			const res = await sdk.SearchPublicDomainImages({ query: p.prompt, jwt });
			return (res.searchPublicDomainImages?.results ?? []).map((r) => ({
				thumbnailUrl: r.thumbnailUrl,
				fullImageUrl: r.fullImageUrl,
				title: r.title,
				source: r.source,
				sourcePageUrl: r.sourcePageUrl,
				license: r.license,
			}));
		}),
	);

	return placeholders.map((p, i) => {
		const result = results[i];
		return {
			prompt: p.prompt,
			results: result.status === "fulfilled" ? result.value : [],
			status: "ready" as const,
		};
	});
}

/** Given user selections, import/generate images and apply replacements */
export async function applyImageSelections(
	code: string,
	placeholders: GeneratePlaceholder[],
	selections: ImagePickerSelection[],
	options: ProcessOptions,
): Promise<string> {
	const { ownerName, stackId, hostId, onImageProgress, onQueueInit } = options;

	// user-skipped placeholders are removed outright: leaving the GENERATE:
	// line behind reads as a failed image and dead-ends the flow (the retry
	// UI only shows for real failures)
	let skippedStripped = code;
	placeholders.forEach((p, i) => {
		if (selections[i] === "skip") {
			skippedStripped = skippedStripped
				.replace(`${p.fullMatch}\n`, "")
				.replace(p.fullMatch, "");
		}
	});

	const toProcess = placeholders
		.map((p, i) => ({ ...p, selection: selections[i], index: i }))
		.filter((p) => p.selection !== "skip");

	if (toProcess.length === 0) return skippedStripped;

	onQueueInit?.(new Array(toProcess.length).fill("generating"));

	const auth = getAuth();
	const jwt = await auth.currentUser?.getIdToken();
	if (!jwt || !ownerName) return skippedStripped;

	const client = new GraphQLClient(clientConfig.gqlGenerateAiImage());
	const sdk = getSdk(client);

	let newCode = skippedStripped;

	const results = await Promise.all(
		toProcess.map(async (item, qi) => {
			try {
				let name: string | null = null;

				if (typeof item.selection === "object" && "generatedName" in item.selection) {
					// already generated inside the picker — just use the name
					name = item.selection.generatedName;
				} else if (typeof item.selection === "object" && "generatePrompt" in item.selection) {
					// AI generation with the user's (possibly edited) prompt
					const res = await sdk.GenerateAiImage({
						prompt: item.selection.generatePrompt || item.prompt,
						jwt,
						ownerName,
						stackId,
					});
					if (res.generateAiImage?.__typename === "QpFile") {
						name = res.generateAiImage.userDefinedName;
					}
				} else if (typeof item.selection === "object") {
					// Import from public domain source
					const res = await sdk.ImportPublicDomainImage({
						fullImageUrl: item.selection.fullImageUrl,
						title: item.selection.title,
						source: item.selection.source,
						jwt,
						ownerName,
						stackId,
					});
					if (res.importPublicDomainImage?.__typename === "QpFile") {
						name = res.importPublicDomainImage.userDefinedName;
					}
				}

				if (name) {
					onImageProgress?.((prev) => {
						const next = [...prev];
						next[qi] = "done";
						return next;
					});
					return { fullMatch: item.fullMatch, keyword: item.keyword, imageModifier: item.imageModifier, name };
				}

				onImageProgress?.((prev) => {
					const next = [...prev];
					next[qi] = "failed";
					return next;
				});
				return null;
			} catch (e) {
				console.error("Image processing error", e);
				onImageProgress?.((prev) => {
					const next = [...prev];
					next[qi] = "failed";
					return next;
				});
				return null;
			}
		}),
	);

	let filesUpdated = false;
	for (const result of results) {
		if (result) {
			newCode = newCode.replace(result.fullMatch, `${result.keyword}: ${result.imageModifier}${result.name}`);
			filesUpdated = true;
		}
	}

	if (filesUpdated) {
		updateFiles(hostId);
	}

	return newCode;
}

/**
 * Legacy: Processes AI-generated Jadle code by replacing GENERATE: image placeholders
 * with actual AI-generated images via the GenerateAiImage SDK call.
 * Used for non-create-mode (where we don't show the picker).
 */
export async function processGeneratedCode(
	jadleCode: string,
	options: ProcessOptions,
): Promise<string> {
	const { code, placeholders } = findGeneratePlaceholders(jadleCode);
	if (placeholders.length === 0) return code;

	// In legacy mode, generate all images directly (no picker)
	const selections: ImagePickerSelection[] = placeholders.map((p) => ({
		generatePrompt: p.prompt,
	}));

	return applyImageSelections(code, placeholders, selections, options);
}
